/**
 * Text-decode ("terminal scramble") primitives.
 *
 * Shared by the eyebrow decode-in effect and the header label ripple. Pure
 * functions — the animation loops that consume them live in the components.
 * Kept dependency-free so unit tests run without jsdom timers or GSAP.
 */

import { clamp01 } from './stack';

/** Code-flavoured glyph pool — the "data" the text decodes out of. Use where
    the rendering font is a full-coverage mono/sans face. */
export const GLYPHS = '01<>{}[]/\\|=+*#$%&@!?;:~^';

/** Alphanumeric pool for display faces (Six Caps, Poppins nav labels) whose
    subsets may lack punctuation glyphs — symbol characters would fall back to
    a different font with wildly different metrics mid-scramble. */
export const SAFE_GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export function randomGlyph(rand: () => number = Math.random, glyphs: string = GLYPHS): string {
  return glyphs[Math.floor(rand() * glyphs.length)];
}

/**
 * One frame of a left-to-right decode. Characters before the progress
 * cutoff show their real value; the rest show random glyphs. Whitespace is
 * never scrambled so word shapes hold. `progress` is clamped to [0, 1];
 * at 1 the input is returned verbatim.
 */
export function decodeFrame(
  text: string,
  progress: number,
  rand: () => number = Math.random,
  glyphs: string = GLYPHS,
): string {
  const p = clamp01(progress);
  if (p >= 1) return text;
  const cutoff = Math.floor(text.length * p);
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    out += i < cutoff || /\s/.test(ch) ? ch : randomGlyph(rand, glyphs);
  }
  return out;
}
